import { INVERTED_PR_KINDS, type Modality, type PrKind } from './enums.js';
import { assistanceG, effectiveLoadG } from './load.js';
import { estimateOneRepMaxG, DEFAULT_ONE_REP_MAX_FORMULA_ID } from './one-rep-max.js';
import { isPrEligible } from './sets.js';
import { setVolumeGramReps } from './volume.js';
import type { CardioSegment, LoggedSet } from './types.js';
import { paceSecondsPerKm } from './cardio.js';

/**
 * Personal records.
 *
 * Comparing raw weight is wrong: 105 kg × 1 and 72.5 kg × 12 are both records
 * and neither beats the other. The model is therefore a **rep-max table** —
 * best load at each rep count — plus whole-exercise records.
 *
 * Everything here is derived. `personal_record` rows are a cache that can be
 * rebuilt from raw sets at any time, which is what makes editing history safe.
 */

/** Which direction counts as better for a given record kind. */
export type Comparator = 'higher' | 'lower';

const INVERTED: ReadonlySet<PrKind> = new Set(INVERTED_PR_KINDS);

export function comparatorFor(kind: PrKind): Comparator {
  // Two inverted cases: needing less assistance, and running a faster pace.
  return INVERTED.has(kind) ? 'lower' : 'higher';
}

/** Strictly better — a tie is not a record. */
export function beats(kind: PrKind, candidate: number, incumbent: number): boolean {
  return comparatorFor(kind) === 'lower' ? candidate < incumbent : candidate > incumbent;
}

/** A record currently held, as loaded from storage. */
export interface ExistingRecord {
  kind: PrKind;
  /** Present for rep-indexed kinds (`rep_max`, `min_assistance`). */
  repCount: number | null;
  value: number;
}

/** A record achieved, carrying enough to find the exact set that produced it. */
export interface RecordCandidate {
  kind: PrKind;
  repCount: number | null;
  value: number;
  unit: 'g' | 'g-reps' | 'reps' | 's' | 'm' | 's/km';
  exerciseId: string;
  sessionId: string;
  /** Null only for cardio records, which come from a segment rather than a set. */
  setId: string | null;
  segmentId: string | null;
  achievedAt: number;
}

export interface DetectRecordsInput {
  exerciseId: string;
  sessionId: string;
  modality: Modality;
  sets: readonly LoggedSet[];
  segments?: readonly CardioSegment[];
  /** Records currently held for this exercise. */
  existing: readonly ExistingRecord[];
  /** Timestamp for records that carry no completion time of their own. */
  at: number;
  oneRepMaxFormulaId?: string;
}

export interface DetectRecordsResult {
  /** New records, strictly better than the incumbent. */
  achieved: RecordCandidate[];
  /** Best value seen this session per kind, whether or not it was a record. */
  bests: RecordCandidate[];
  /** Sets excluded as warm-ups, drop stages or incomplete. */
  excludedSets: number;
}

const keyOf = (kind: PrKind, repCount: number | null): string =>
  `${kind}#${repCount === null ? '-' : String(repCount)}`;

/** Keep only the better of two candidates for the same key. */
function betterOf(kind: PrKind, a: RecordCandidate, b: RecordCandidate): RecordCandidate {
  return beats(kind, b.value, a.value) ? b : a;
}

/**
 * Detect records set by one session's work on one exercise.
 *
 * Which kinds apply depends on how the exercise is loaded — a bodyweight dip
 * progresses by reps, an assisted pull-up by needing less help, a plank by
 * time. Applying rep-max logic to all of them would produce meaningless records.
 */
export function detectPersonalRecords(input: DetectRecordsInput): DetectRecordsResult {
  const formulaId = input.oneRepMaxFormulaId ?? DEFAULT_ONE_REP_MAX_FORMULA_ID;
  const bestByKey = new Map<string, RecordCandidate>();
  let excludedSets = 0;

  const offer = (candidate: RecordCandidate): void => {
    const key = keyOf(candidate.kind, candidate.repCount);
    const current = bestByKey.get(key);
    bestByKey.set(key, current === undefined ? candidate : betterOf(candidate.kind, current, candidate));
  };

  for (const set of input.sets) {
    if (!isPrEligible(set)) {
      excludedSets++;
      continue;
    }

    const base = {
      exerciseId: input.exerciseId,
      sessionId: input.sessionId,
      setId: set.id,
      segmentId: null,
      achievedAt: set.completedAt ?? input.at,
    };

    // ── Duration holds: time under tension is the performance.
    if (set.durationS !== null && set.durationS > 0 && (input.modality === 'hold' || set.reps === null)) {
      offer({ ...base, kind: 'max_duration', repCount: null, value: set.durationS, unit: 's' });
      continue;
    }

    if (set.reps === null || set.reps <= 0) continue;

    // ── Assisted: progress is measured by needing less help, and remains
    //    measurable even when bodyweight was never recorded.
    if (set.loadType === 'assisted') {
      const assistance = assistanceG(set);
      if (assistance.known) {
        offer({
          ...base,
          kind: 'min_assistance',
          repCount: set.reps,
          value: assistance.value,
          unit: 'g',
        });
      }
      continue;
    }

    // ── Pure bodyweight with no added load: reps are the only variable.
    if (set.loadType === 'bodyweight') {
      offer({ ...base, kind: 'max_reps', repCount: null, value: set.reps, unit: 'reps' });
      continue;
    }

    // ── Loaded work: rep-max table, estimated 1RM, and best single-set volume.
    const load = effectiveLoadG(set);
    if (!load.known) continue;

    offer({ ...base, kind: 'rep_max', repCount: set.reps, value: load.value, unit: 'g' });
    offer({ ...base, kind: 'max_weight', repCount: null, value: load.value, unit: 'g' });

    const oneRepMax = estimateOneRepMaxG(load.value, set.reps, formulaId);
    if (oneRepMax !== null) {
      offer({ ...base, kind: 'est_1rm', repCount: null, value: oneRepMax, unit: 'g' });
    }

    const volume = setVolumeGramReps(set);
    if (volume !== null) {
      offer({ ...base, kind: 'max_volume_set', repCount: null, value: volume, unit: 'g-reps' });
    }
  }

  // ── Cardio records come from segments, not sets.
  for (const segment of input.segments ?? []) {
    if (!segment.isCompleted) continue;

    const base = {
      exerciseId: input.exerciseId,
      sessionId: input.sessionId,
      setId: null,
      segmentId: segment.id,
      achievedAt: input.at,
    };

    if (segment.distanceM !== null && segment.distanceM > 0) {
      offer({ ...base, kind: 'max_distance', repCount: null, value: segment.distanceM, unit: 'm' });
    }
    if (segment.durationS !== null && segment.durationS > 0) {
      offer({ ...base, kind: 'max_duration', repCount: null, value: segment.durationS, unit: 's' });
    }

    const pace = paceSecondsPerKm(segment.distanceM, segment.durationS);
    if (pace.known) {
      // Seconds per kilometre: lower is faster, handled by the inverted
      // comparator rather than by negating the stored value.
      offer({ ...base, kind: 'best_pace', repCount: null, value: pace.value, unit: 's/km' });
    }
  }

  const existingByKey = new Map(input.existing.map((r) => [keyOf(r.kind, r.repCount), r.value]));
  const bests = [...bestByKey.values()];
  const achieved = bests.filter((candidate) => {
    const incumbent = existingByKey.get(keyOf(candidate.kind, candidate.repCount));
    // No incumbent means it is a record by definition.
    return incumbent === undefined || beats(candidate.kind, candidate.value, incumbent);
  });

  return { achieved, bests, excludedSets };
}

/**
 * Build a rep-max table from a set of records: best load at 1, 2, 3 … reps.
 * This is the view that makes a PR more than "heaviest weight ever".
 */
export function buildRepMaxTable(
  records: readonly ExistingRecord[],
): Array<{ reps: number; value: number }> {
  return records
    .filter((r) => r.kind === 'rep_max' && r.repCount !== null)
    .map((r) => ({ reps: r.repCount as number, value: r.value }))
    .sort((a, b) => a.reps - b.reps);
}

/**
 * Whether a record at `reps` is implied by a heavier record at more reps.
 *
 * Lifting 100 kg for 8 makes a 90 kg × 5 "record" uninteresting. Used to keep
 * the displayed table honest rather than to discard stored data.
 */
export function isDominated(
  candidate: { reps: number; value: number },
  table: ReadonlyArray<{ reps: number; value: number }>,
): boolean {
  return table.some((entry) => entry.reps >= candidate.reps && entry.value >= candidate.value && !(entry.reps === candidate.reps && entry.value === candidate.value));
}
